import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Circle, ArrowRight, Settings, Users, Sparkles, Mail, Database, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GettingStarted = () => {
  const navigate = useNavigate();
  const [setupStatus, setSetupStatus] = useState({
    gingrConnected: false,
    gmailConnected: false,
    teamInvited: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Check integrations
      const integrationsResponse = await axios.get(`${API}/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const integrations = integrationsResponse.data;
      const gingrConnected = integrations.some(i => i.type === 'gingr' && i.status === 'active');
      const gmailConnected = integrations.some(i => i.type === 'gmail_imap' && i.status === 'active');
      
      // Check if team members invited (more than just the admin)
      const usersResponse = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const teamInvited = usersResponse.data.length > 1;
      
      setSetupStatus({
        gingrConnected,
        gmailConnected,
        teamInvited
      });
    } catch (error) {
      console.error('Error checking setup status:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupSteps = [
    {
      id: 'gingr',
      title: 'Connect Gingr POS',
      description: 'Sync your reservations and customer data',
      icon: Database,
      completed: setupStatus.gingrConnected,
      required: true,
      action: () => navigate('/settings'),
      actionLabel: 'Go to Settings',
      instructions: [
        'Go to Settings → Integrations',
        'Find the Gingr POS section',
        'Enter your Gingr App URL (e.g., yourcompany.gingrapp.com)',
        'Enter your Gingr API Key (get from Gingr Settings → API)',
        'Click "Save & Test Connection"'
      ],
      helpLink: 'https://support.gingrapp.com/hc/en-us/articles/api-access'
    },
    {
      id: 'gmail',
      title: 'Connect Gmail (Optional)',
      description: 'Auto-capture leads from contact forms',
      icon: Mail,
      completed: setupStatus.gmailConnected,
      required: false,
      action: () => navigate('/settings'),
      actionLabel: 'Go to Settings',
      instructions: [
        'Go to Settings → Integrations',
        'Find the Gmail IMAP section',
        'Generate Gmail App Password at myaccount.google.com/apppasswords',
        'Enter your Gmail address and App Password',
        'Click "Connect Gmail"'
      ],
      helpLink: 'https://support.google.com/accounts/answer/185833'
    },
    {
      id: 'team',
      title: 'Invite Your Team',
      description: 'Add staff members to help manage leads',
      icon: Users,
      completed: setupStatus.teamInvited,
      required: false,
      action: () => navigate('/admin/users'),
      actionLabel: 'Invite Users',
      instructions: [
        'Go to Users → Invite User',
        'Enter team member email',
        'Assign role (Manager or Agent)',
        'They\'ll receive an invite email'
      ]
    },
    {
      id: 'ai',
      title: 'Try AI Insights',
      description: 'Ask questions about your business',
      icon: Sparkles,
      completed: false,
      required: false,
      action: () => navigate('/ai-insights'),
      actionLabel: 'Try AI Insights',
      instructions: [
        'Go to AI Insights',
        'Ask questions like:',
        '  • "How many dogs checking in today?"',
        '  • "Show me new leads this week"',
        '  • "What\'s my revenue for this month?"',
        'AI will analyze your data and respond'
      ]
    }
  ];

  const completedSteps = setupSteps.filter(s => s.completed).length;
  const totalSteps = setupSteps.length;
  const progressPercent = (completedSteps / totalSteps) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading setup status...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-1 sm:mb-2" style={{ fontFamily: 'Manrope' }}>Welcome to LeadGen Tracker!</h1>
        <p className="text-sm sm:text-lg text-gray-600">Let's get your location set up in a few simple steps</p>
      </div>

      {/* Progress Bar */}
      <Card className="mb-8 bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Setup Progress</span>
            <span className="text-sm font-semibold text-blue-600">{completedSteps} of {totalSteps} completed</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-600 to-cyan-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {completedSteps === totalSteps && (
            <p className="text-green-600 font-medium mt-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              All done! You're ready to start tracking leads
            </p>
          )}
        </CardContent>
      </Card>

      {/* Setup Steps */}
      <div className="space-y-4">
        {setupSteps.map((step, index) => {
          const Icon = step.icon;
          return (
            <Card key={step.id} className={step.completed ? 'border-green-300 bg-green-50/30' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      step.completed 
                        ? 'bg-green-100' 
                        : 'bg-blue-100'
                    }`}>
                      {step.completed ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <Icon className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">
                          {step.title}
                          {step.required && (
                            <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Required</span>
                          )}
                        </CardTitle>
                      </div>
                      <CardDescription className="mt-1">{step.description}</CardDescription>
                    </div>
                  </div>
                  {!step.completed && (
                    <Button onClick={step.action} className="flex-shrink-0">
                      {step.actionLabel}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  {step.completed && (
                    <span className="text-green-600 font-medium flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Complete
                    </span>
                  )}
                </div>
              </CardHeader>
              {!step.completed && (
                <CardContent>
                  <div className="ml-16">
                    <h4 className="font-semibold text-gray-900 mb-2">How to set this up:</h4>
                    <ol className="space-y-2">
                      {step.instructions.map((instruction, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-blue-600 font-medium">{idx + 1}.</span>
                          <span>{instruction}</span>
                        </li>
                      ))}
                    </ol>
                    {step.helpLink && (
                      <a 
                        href={step.helpLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-3"
                      >
                        Need help? View documentation
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Jump to common tasks</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button variant="outline" onClick={() => navigate('/')} className="h-auto py-4 flex flex-col items-center gap-2">
            <Settings className="w-6 h-6" />
            <span>View Dashboard</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/leads')} className="h-auto py-4 flex flex-col items-center gap-2">
            <Users className="w-6 h-6" />
            <span>View Leads</span>
          </Button>
          <Button variant="outline" onClick={() => navigate('/settings')} className="h-auto py-4 flex flex-col items-center gap-2">
            <Settings className="w-6 h-6" />
            <span>Settings</span>
          </Button>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">
            If you have questions or run into issues during setup, we're here to help!
          </p>
          <Button variant="outline" onClick={() => navigate('/contact-us')}>
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default GettingStarted;
